import { api } from "@/lib/api/client";
import type {
  Facility,
  Invitation,
  Item,
  ItemAttachmentRef,
  ItemCategory,
  MaintainerInviteState,
  Membership,
  Paginated,
  PresignedUpload,
} from "./types";

/** Write shape for items — read shape `Item.attachments` is replaced by
 * the write-side `attachments` ref list, so we omit it from Partial<Item>
 * and re-add the ref list explicitly. */
export type ItemWritePayload = Partial<Omit<Item, "attachments">> & {
  attachments?: ItemAttachmentRef[];
};

export const facilitiesApi = {
  list: () => api.get<Paginated<Facility>>("/api/facilities/"),
  get: (id: number) => api.get<Facility>(`/api/facilities/${id}/`),
  create: (data: Partial<Facility>) => api.post<Facility>("/api/facilities/", data),
  update: (id: number, data: Partial<Facility>) =>
    api.patch<Facility>(`/api/facilities/${id}/`, data),
  destroy: (id: number) => api.delete<void>(`/api/facilities/${id}/`),
  /** Self-serve onboarding: spin up the boat-club demo facility for
   * the current user. Idempotent. */
  seedDemo: () => api.post<Facility>("/api/facilities/seed-demo/"),

  listItems: (facilityId: number) =>
    api.get<Paginated<Item>>(`/api/items/?facility=${facilityId}`),
  getItem: (id: number) => api.get<Item>(`/api/items/${id}/`),
  createItem: (data: ItemWritePayload) => api.post<Item>("/api/items/", data),
  updateItem: (id: number, data: ItemWritePayload) =>
    api.patch<Item>(`/api/items/${id}/`, data),
  destroyItem: (id: number) => api.delete<void>(`/api/items/${id}/`),

  /**
   * Maintainer-side presigned PUT for the walk-the-facility flow.
   * Returns `{ url, key, headers }`. Caller uploads the blob to `url`
   * with method PUT and the given headers, then submits the `key` to
   * createItem under `attachments[]`.
   */
  presignFacilityUpload: (
    facilityId: number,
    contentType: string,
    size: number,
  ) =>
    api.post<PresignedUpload>(`/api/facilities/${facilityId}/uploads/`, {
      content_type: contentType,
      size,
    }),

  listCategories: (facilityId: number) =>
    api.get<Paginated<ItemCategory>>(`/api/item-categories/?facility=${facilityId}`),
  createCategory: (data: Partial<ItemCategory>) =>
    api.post<ItemCategory>("/api/item-categories/", data),
  destroyCategory: (id: number) => api.delete<void>(`/api/item-categories/${id}/`),

  listMemberships: (facilityId: number) =>
    api.get<Paginated<Membership>>(`/api/memberships/?facility=${facilityId}`),
  updateMembership: (id: number, data: Partial<Membership>) =>
    api.patch<Membership>(`/api/memberships/${id}/`, data),
  destroyMembership: (id: number) =>
    api.delete<void>(`/api/memberships/${id}/`),

  listInvitations: (facilityId: number) =>
    api.get<Paginated<Invitation>>(`/api/invitations/?facility=${facilityId}`),
  createInvitation: (facility: number, email: string, role = "maintainer") =>
    api.post<Invitation>("/api/invitations/", { facility, email, role }),
  destroyInvitation: (id: number) =>
    api.delete<void>(`/api/invitations/${id}/`),
  acceptInvitation: (token: string) =>
    api.post<{ facility: number; status: string }>("/api/invitations/accept/", {
      token,
    }),

  // ─── Shareable maintainer invite link ──────────────────────────────────
  getMaintainerInvite: (facilityId: number) =>
    api.get<MaintainerInviteState>(
      `/api/facilities/${facilityId}/maintainer-invite/`,
    ),
  enableMaintainerInvite: (facilityId: number) =>
    api.post<MaintainerInviteState>(
      `/api/facilities/${facilityId}/maintainer-invite/`,
    ),
  disableMaintainerInvite: (facilityId: number) =>
    api.delete<MaintainerInviteState>(
      `/api/facilities/${facilityId}/maintainer-invite/`,
    ),

  approveMembership: (id: number) =>
    api.post<Membership>(`/api/memberships/${id}/approve/`),
  rejectMembership: (id: number) =>
    api.post<void>(`/api/memberships/${id}/reject/`),

  // ─── Public claim ───────────────────────────────────────────────────────
  lookupMaintainerInvite: (token: string) =>
    api.get<{
      facility_id: number;
      facility_name: string;
      requires_approval: boolean;
    }>(`/api/public/maintainer-invite/${encodeURIComponent(token)}/`),
  claimMaintainerInvite: (
    token: string,
    name: string,
    email: string,
    password?: string,
  ) =>
    api.post<{ status: "active" | "pending"; facility: number }>(
      "/api/public/maintainer-claim/",
      { token, name, email, password },
    ),

  // Poll target for pending volunteers — works even while the
  // membership is still PENDING and the rest of the facility API is 403.
  getMyMembership: (facilityId: number) =>
    api.get<{ role: "owner" | "maintainer"; status: "active" | "pending" }>(
      `/api/facilities/${facilityId}/my-membership/`,
    ),
};
